import { groupUnavailableOriginal } from "@/utils/stockGrouping";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmt, BrandBadge } from "@/utils/format";
import StockBrandLogo from "@/components/StockBrandLogo";
import StockBarcode from "@/components/StockBarcode";
import VehicleCatalogFilter from "@/components/VehicleCatalogFilter";
import useDesktopLayout from "@/hooks/useDesktopLayout";
import { STOK } from "@/constants/testIds";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { applyPastedStockSearch, isStockSearchReady, isStockSearchSubmitReady, normalizeStockSearch } from "@/utils/stockSearch";
import {
  Search, Plus, Pencil, Trash2, Target, X,
  ChevronLeft, ChevronRight, ArrowUp,
  XCircle, CheckCircle2, AlertCircle,
  ChevronDown, Package, Star, BadgePercent, ShoppingCart,
} from "lucide-react";

// ── Pricing constants ────────────────────────────────────────────────────────
const KDV             = 0.20;
const _EUR_CURRENCIES = new Set(["EUR", "€", "EURO"]);
const _ORIJINAL_BRANDS = new Set(["VW ORİJİNAL", "PORSCHE ORİJİNAL"]);
const OEM_CHAR_LIMIT  = 60;
const MOBILE_OEM_CHAR_LIMIT = 14;
const hasOemValue = (value) => Boolean(String(value || "").trim());
const hasNoOemMatch = (item) => !hasOemValue(item?.oem_no1) && !hasOemValue(item?.oem_no2);

function ResponsiveOemValue({ value }) {
  if (!value) return "—";
  return (
    <>
      <span className="sm:hidden">{value.slice(0, MOBILE_OEM_CHAR_LIMIT)}{value.length > MOBILE_OEM_CHAR_LIMIT ? "…" : ""}</span>
      <span className="hidden sm:inline">{value.slice(0, OEM_CHAR_LIMIT)}{value.length > OEM_CHAR_LIMIT ? "…" : ""}</span>
    </>
  );
}

function LogoSettingsLink({ brand, onOpen, className = "" }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(brand)}
      className={`group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D1B3E] focus-visible:ring-offset-2 ${className}`}
      title={`${brand || "Diğer"} logo ayarlarını aç`}
      aria-label={`${brand || "Diğer"} logo ayarlarını aç`}
    >
      <StockBrandLogo brand={brand} className="transition-colors group-hover:border-[#0D1B3E]/50" />
    </button>
  );
}

const DEFAULT_FL = [
  { ad: "Perakende", oran: 0.15 },
  { ad: "Esnaf",     oran: 0.20 },
  { ad: "Servis",    oran: 0.25 },
];

/** Compute all 3 group prices for a stok item. Returns null when no price data. */
function calcPrices(stok) {
  return stok.liste_fiyatlari || null;
}

// ── Stock badge (birebir B2BStok.js) ────────────────────────────────────────
const STOCK_TOOLTIP = {
  out:  "Siparişle temin edebilirsiniz.",
  last: "Son kalan 1 adet",
  in:   "2+ Stok var",
};

function StockBadge({ qty }) {
  const n = qty ?? 0;
  const cfg =
    n <= 0
      ? { Icon: XCircle,      iconCls: "text-red-500",   labelCls: "text-red-500",   label: ["Stokta","Yok"],  tip: STOCK_TOOLTIP.out  }
      : n === 1
      ? { Icon: AlertCircle,  iconCls: "text-amber-500", labelCls: "text-amber-600", label: ["Stokta","Var"],  tip: STOCK_TOOLTIP.last }
      : { Icon: CheckCircle2, iconCls: "text-green-500", labelCls: "text-green-600", label: ["Stokta","Var"],  tip: STOCK_TOOLTIP.in   };
  const { Icon, iconCls, labelCls, label, tip } = cfg;
  return (
    <div className="relative group flex flex-col items-center gap-0.5 w-16 shrink-0 cursor-default select-none">
      <Icon size={26} strokeWidth={2} className={iconCls} />
      <span className={`text-[10px] font-bold ${labelCls} uppercase tracking-wide text-center leading-tight`}>
        {label[0]}<br />{label[1]}
      </span>
      <div className="pointer-events-none absolute bottom-full mb-2.5 left-1/2 -translate-x-1/2 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 z-50 whitespace-nowrap bg-slate-900 text-white text-xs font-medium rounded-xl px-3 py-2 shadow-2xl border border-white/10">
        {tip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-slate-900" />
      </div>
    </div>
  );
}

// ── Brand dropdown (birebir B2BStok.js) ─────────────────────────────────────
function BrandDropdown({ brands, selected, onSelect, loading }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(""); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = brands.filter((b) => b.toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
          selected ? "border-[#0D1B3E] bg-[#0D1B3E]/5 text-[#0D1B3E]" : "border-slate-300 bg-white text-slate-500 hover:border-slate-400"
        }`}>
        <span className="flex items-center gap-2 min-w-0">
          <Package size={14} className={selected ? "text-[#0D1B3E]" : "text-slate-400"} />
          <span className="truncate">{selected || "Tüm Markalar"}</span>
        </span>
        <ChevronDown size={14} className={`shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
          <div className="p-2.5 border-b border-slate-100">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Marka ara..."
                className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#0D1B3E]/40 focus:ring-1 focus:ring-[#0D1B3E]/20 transition-colors" />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto overscroll-contain">
            <button onClick={() => { onSelect(""); setOpen(false); setQuery(""); }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors hover:bg-slate-50 ${!selected ? "font-semibold text-[#0D1B3E]" : "text-slate-600"}`}>
              <span>Tüm Markalar</span>
              {!selected && <CheckCircle2 size={14} className="text-[#0D1B3E]" />}
            </button>
            <div className="h-px bg-slate-100 mx-3" />
            {loading ? (
              <div className="px-4 py-4 text-center text-slate-400 text-sm">Yükleniyor...</div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-4 text-center text-slate-400 text-sm">Marka bulunamadı</div>
            ) : filtered.map((b) => (
              <button key={b} onClick={() => { onSelect(b); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors hover:bg-slate-50 ${selected === b ? "font-semibold text-[#0D1B3E] bg-slate-50/80" : "text-slate-700"}`}>
                <span className="truncate">{b}</span>
                {selected === b && <CheckCircle2 size={14} className="text-[#0D1B3E] shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Empty form for create/edit modal ────────────────────────────────────────
const EMPTY_FORM = {
  kod: "", ad: "", marka: "", birim: "Adet",
  stok_miktari: 0, paket_adedi: 1, para_birimi: "TL", tl_fiyat: null, eur_fiyat: null,
  barkod: "", oem_kodu: "", oem_no1: "", degisen_no: "", ek_bilgi: "", oem_no2: "",
  vitrin: false, vitrin_sira: 0, kampanya_iskonto: 0,
};

// ── Main component ───────────────────────────────────────────────────────────
export default function StokList() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const importedIds = urlParams.get("imported") || "";
  const [stoklar, setStoklar]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [pages, setPages]             = useState(1);
  const initialSearch = normalizeStockSearch(urlParams.get("search") || "");
  const [search, setSearch]           = useState(initialSearch);
  const [activeSearch, setActiveSearch] = useState(isStockSearchReady(initialSearch) ? initialSearch : "");
  const [brandFilter, setBrandFilter] = useState("");
  const [inStock, setInStock]         = useState(false);
  const [vitrinOnly, setVitrinOnly]   = useState(false);
  const [brands, setBrands]           = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [loading, setLoading]         = useState(false);
  // Pricing
  const [eurKuru, setEurKuru]             = useState(38.5);
  const [fiyatListeleri, setFiyatListeleri] = useState(DEFAULT_FL);
  // Dynamic X position
  const [xLeft, setXLeft]     = useState(0);
  const mirrorRef             = useRef(null);
  const searchInputRef        = useRef(null);
  const loadMoreRef           = useRef(null);
  const requestRef            = useRef(null);
  const openLogoSettings = useCallback((brand) => {
    navigate(`/ayarlar?logoMarka=${encodeURIComponent(String(brand || "").trim())}#marka-logolari`);
  }, [navigate]);
  // CRUD modal
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [oemModal, setOemModal]   = useState(null);
  const [alternativeCount, setAlternativeCount] = useState(0);
  const [alternativesOnly, setAlternativesOnly] = useState(false);
  const [catalogFilter, setCatalogFilter] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [verifyingOem, setVerifyingOem] = useState(false);
  const [quantities, setQuantities] = useState({});
  const { addToCart, cart } = useCart();
  const { user } = useAuth();
  const canSuggestOem = ["admin", "satis_destek"].includes(user?.role);
  const isDesktop = useDesktopLayout();

  const isExactMatch = search.trim().endsWith(",");
  const displayedStoklar = activeSearch.trim() ? groupUnavailableOriginal(stoklar, activeSearch) : stoklar;
  const getPack = (item) => Math.max(1, parseInt(item?.paket_adedi, 10) || 1);
  const getQty = (item) => quantities[item.id] ?? getPack(item);
  const setQty = (item, raw) => {
    const pack = getPack(item);
    const value = Math.max(pack, Math.ceil((parseInt(raw, 10) || pack) / pack) * pack);
    setQuantities((previous) => ({ ...previous, [item.id]: value }));
  };
  const isInCart = (id) => cart.some((item) => item.stok_id === id);
  const handleAddToCart = (item, prices) => {
    const stockQuantity = Number(item._effectiveStock ?? item.stok_miktari ?? 0);
    const isBackorder = stockQuantity <= 0;
    if (isBackorder && !window.confirm("Bu ürün stokta bulunmuyor. Bakiyeye almak istiyor musunuz?")) return;
    const servicePrice = prices?.find(({ label }) => String(label).toLocaleLowerCase("tr-TR") === "servis")?.price
      ?? prices?.[prices.length - 1]?.price ?? 0;
    addToCart({
      stok_id: item.id,
      stok_kodu: item.kod,
      stok_adi: item.ad,
      birim_fiyat: servicePrice,
      toplam: servicePrice,
      b2b_fiyat: servicePrice,
      paket_adedi: getPack(item),
      stok_miktari: stockQuantity,
      bakiye_talebi: isBackorder,
    }, getQty(item));
    toast.success(`${getQty(item)} adet ${item.kod} ${isBackorder ? "bakiyeli olarak " : ""}sepete eklendi`);
    setQty(item, getPack(item));
  };

  // Measure text width for dynamic X
  useEffect(() => {
    if (!mirrorRef.current) return;
    const w      = mirrorRef.current.offsetWidth;
    const inputW = searchInputRef.current?.offsetWidth ?? 380;
    setXLeft(Math.min(40 + w + 6, inputW - 32));
  }, [search]);

  // Load pricing data + brands on mount
  useEffect(() => {
    Promise.all([
      api.get("/fiyat-listeleri/"),
      api.get("/settings/eur-kuru"),
      api.get("/stoklar/brands"),
    ]).then(([flRes, eurRes, brRes]) => {
      setFiyatListeleri(flRes.data || DEFAULT_FL);
      setEurKuru(eurRes.data?.kur || 38.5);
      setBrands(brRes.data?.brands || []);
    }).catch((e) => console.error("Pricing/brands load error:", e))
      .finally(() => setBrandsLoading(false));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setActiveSearch(isStockSearchReady(search) ? search : "");
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const groupingSearch = Boolean(activeSearch.trim());
      const params = { page, per_page: groupingSearch ? 10 : 15 };
      if (activeSearch) params.search  = activeSearch;
      else if (vitrinOnly && !importedIds) params.vitrin_only = true;
      if (brandFilter) params.marka    = brandFilter;
      if (inStock)     params.in_stock = true;
      if (importedIds) params.ids = importedIds;
      if (alternativesOnly) params.alternatives_only = true;
      const r = await api.get("/stoklar/", { params, signal: controller.signal });
      const originals = page === 1 ? (r.data.originals || []) : [];
      setStoklar((previous) => page === 1 || !groupingSearch
        ? [...originals, ...r.data.items]
        : [...previous, ...r.data.items]);
      setTotal(r.data.total);
      setPages(r.data.pages || 1);
      if (page === 1 && !alternativesOnly) setAlternativeCount(r.data.available_alternative_count || 0);
    } catch (e) {
      if (e.code === "ERR_CANCELED") return;
      toast.error(formatApiError(e));
    } finally {
      if (requestRef.current === controller) setLoading(false);
    }
  }, [page, activeSearch, brandFilter, inStock, importedIds, vitrinOnly, alternativesOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => () => requestRef.current?.abort(), []);
  useEffect(() => { setStoklar([]); setPage(1); }, [activeSearch, brandFilter, inStock, vitrinOnly, alternativesOnly]);
  useEffect(() => { setAlternativesOnly(false); setAlternativeCount(0); }, [activeSearch, brandFilter, inStock, vitrinOnly, importedIds]);
  useEffect(() => {
    if (!activeSearch || page >= pages || !loadMoreRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loading) setPage((value) => value + 1);
    }, { rootMargin: "240px" });
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [activeSearch, page, pages, loading]);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit   = (s) => { setEditItem(s); setForm({ ...EMPTY_FORM, ...s }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editItem) { await api.put(`/stoklar/${editItem.id}`, form); toast.success("Stok güncellendi"); }
      else          { await api.post("/stoklar/", form);              toast.success("Stok oluşturuldu"); }
      setShowModal(false);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally     { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu stoku silmek istediğinizden emin misiniz?")) return;
    try { await api.delete(`/stoklar/${id}`); toast.success("Stok silindi"); fetchData(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openSuggestion = () => {
    if (!catalogFilter) return toast.error("Önce ilgili aracı ve ürün grubunu seçin");
    setSuggestion({ action: "primary_oem", oem: "", note: "" });
  };

  const saveSuggestion = async () => {
    if (!suggestion || !catalogFilter) return;
    try {
      await api.post("/catalog/suggestions", {
        type_nos: catalogFilter.typeNos || [], engine_code: catalogFilter.engineCode || null,
        category: catalogFilter.category, subcategory: catalogFilter.subcategory || null, detail: catalogFilter.detail || null,
        action: "primary_oem", oem: suggestion.oem.trim(), stock_code: "",
        vehicle_label: catalogFilter.vehicleLabel || "",
        note: [catalogFilter.primaryOem ? `Mevcut arama OEM'i: ${catalogFilter.primaryOem}` : "Bu seçimde arama OEM'i bulunamadı", suggestion.note.trim()].filter(Boolean).join(" · "),
      });
      toast.success("OEM eşleşme önerisi bekleyenlere eklendi");
      setSuggestion(null);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const verifyPrimaryOem = async () => {
    if (!catalogFilter?.primaryOem || !canSuggestOem) return;
    setVerifyingOem(true);
    try {
      await api.post("/catalog/admin/override", {
        type_nos: catalogFilter.typeNos || [], engine_code: catalogFilter.engineCode || null,
        category: catalogFilter.category, subcategory: catalogFilter.subcategory || null, detail: catalogFilter.detail || null,
        action: "primary_oem", value: catalogFilter.primaryOem,
        note: "Stok Yönetimi ekranında doğru OEM olarak doğrulandı",
      });
      setCatalogFilter((current) => current ? {
        ...current, primaryOemVerified: true, primaryOemVerificationRole: user?.role || "",
        primaryOemVerifiedBy: user?.email || user?.name || "", primaryOemVerifiedAt: new Date().toISOString(),
      } : current);
      toast.success("OEM doğrulandı ve doğru rozeti verildi");
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setVerifyingOem(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">

      {/* Başlık + Yeni Stok butonu */}
      <div className="px-5 pt-5 pb-2 bg-slate-50 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Stok Yönetimi
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {total.toLocaleString("tr-TR")} kayıt · EUR: {fmt(eurKuru, 4)} · KDV dahil fiyatlar
          </p>
        </div>
        <button
          data-testid={STOK.createButton}
          onClick={openCreate}
          className="shrink-0 flex items-center gap-2 bg-[#0D1B3E] text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-[#172A58] active:scale-[0.98] transition-all"
        >
          <Plus size={15} /> Yeni Stok
        </button>
      </div>

      <VehicleCatalogFilter
        value={catalogFilter}
        onApply={async (selection) => {
          try {
            const { data } = await api.post("/catalog/resolve", {
              type_nos: selection.typeNos || [], engine_code: selection.engineCode || null,
              category: selection.category, subcategory: selection.subcategory || null, detail: selection.detail || null,
            });
            setBrandFilter(""); setAlternativesOnly(false); setPage(1);
            setCatalogFilter({ ...selection, primaryOem: data.primary_oem || "", primaryOemVerified: data.verified_by_human, primaryOemVerificationRole: data.verification_role, primaryOemVerifiedBy: data.verified_by, primaryOemVerifiedAt: data.verified_at });
            if (!data.primary_oem) {
              setSearch(""); setActiveSearch("");
              return toast.error("Bu araç ve alt grup için aranacak OEM kodu bulunamadı");
            }
            setSearch(data.primary_oem); setActiveSearch(data.primary_oem);
          } catch (error) { toast.error(formatApiError(error)); }
        }}
        onClear={() => { setCatalogFilter(null); setSearch(""); setActiveSearch(""); setAlternativesOnly(false); }}
      />

      {canSuggestOem && catalogFilter && <div className={`mx-5 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${catalogFilter.primaryOem ? "border-blue-200 bg-blue-50 text-blue-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
        <div><b>{catalogFilter.category}{catalogFilter.subcategory ? ` / ${catalogFilter.subcategory}` : ""}{catalogFilter.detail ? ` / ${catalogFilter.detail}` : ""}</b><div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">{catalogFilter.primaryOem ? <><span>Aramayı başlatan OEM: <span className="font-mono font-bold">{catalogFilter.primaryOem}</span></span>{catalogFilter.primaryOemVerified && <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 font-bold text-amber-800" title={`${catalogFilter.primaryOemVerifiedBy || "Personel"} tarafından doğrulandı`}><Star size={12} fill="currentColor"/>Doğru OEM</span>}</> : "Bu araç ve alt grup için aramayı başlatacak OEM bulunamadı."}</div></div>
        <div className="flex flex-wrap gap-2">{catalogFilter.primaryOem && !catalogFilter.primaryOemVerified && <button type="button" disabled={verifyingOem} onClick={verifyPrimaryOem} className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-100 px-4 py-2 text-xs font-bold text-amber-900 disabled:opacity-50"><Star size={14}/>{verifyingOem ? "Doğrulanıyor..." : "Doğru OEM · Rozet ver"}</button>}<button type="button" onClick={openSuggestion} className={`rounded-lg px-4 py-2 text-xs font-bold text-white ${catalogFilter.primaryOem ? "bg-blue-600" : "bg-amber-500"}`}>{catalogFilter.primaryOem ? "Arama OEM'i için değişiklik öner" : "Arama OEM'i öner"}</button></div>
      </div>}

      {/* ── Sticky filtre çubuğu ─────────────────────────────────────────────── */}
      <div className={`${catalogFilter ? "" : "sm:sticky sm:top-16"} z-20 bg-white/96 backdrop-blur-md border-b border-slate-200 shadow-sm px-5 py-3 space-y-2.5`}>

        {/* Arama — dinamik X */}
        <form className="relative" onSubmit={(e) => { e.preventDefault(); if (isStockSearchSubmitReady(search)) setActiveSearch(normalizeStockSearch(search).trim()); else toast.error("Enter ile arama için en az 3 karakter girin"); }}>
          <span ref={mirrorRef} aria-hidden="true"
            className="absolute left-10 top-3 text-sm whitespace-pre opacity-0 pointer-events-none select-none"
            style={{ fontFamily: "inherit" }}>
            {search}
          </span>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            ref={searchInputRef}
            data-testid={STOK.searchInput}
            value={search}
            onChange={(e) => setSearch(normalizeStockSearch(e.target.value))}
            onPaste={(e) => {
              e.preventDefault();
              const input = e.currentTarget;
              setSearch(applyPastedStockSearch(search, e.clipboardData.getData("text"), input.selectionStart, input.selectionEnd));
            }}
            placeholder="OEM No, Stok Kodu, Stok Adı veya Barkod Ara...  (sonda virgül = kesin eşleşme)"
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-colors ${
              isExactMatch ? "border-amber-400 bg-amber-50 focus:ring-amber-300/40" : "border-slate-300 bg-white focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E]"
            }`}
          />
          {search && !isExactMatch && (
            <button type="button" onClick={() => setSearch("")} title="Aramayı temizle"
              className="absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-900 transition-colors"
              style={{ left: `${xLeft}px` }}>
              <X size={13} strokeWidth={2.5} />
            </button>
          )}
          {isExactMatch && (
            <button type="button" onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-amber-100 text-amber-700 text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-300 hover:bg-amber-200 transition-colors">
              <Target size={11} /> Kesin ×
            </button>
          )}
          {search && !isStockSearchReady(search) && (
            <p className="pt-1 pl-1 text-[11px] text-slate-400">Otomatik arama 6 karakterde başlar; 3 karakterden sonra Enter ile arayabilirsiniz.</p>
          )}
        </form>

        {/* Stokta Var + Marka Dropdown */}
        <div className="flex items-center gap-2">
          <button onClick={() => setVitrinOnly((v) => !v)} className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all ${vitrinOnly ? "bg-amber-500 border-amber-500 text-white" : "border-slate-300 bg-white text-slate-600"}`}><Star size={15}/> {vitrinOnly ? "Vitrin" : "Tüm Stoklar"}</button>
          <button onClick={() => setInStock((v) => !v)}
            className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-150 ${
              inStock ? "bg-green-500 border-green-500 text-white shadow-sm" : "border-slate-300 bg-white text-slate-600 hover:border-green-400 hover:text-green-600"
            }`}>
            <CheckCircle2 size={15} strokeWidth={2.5} className={inStock ? "text-white" : "text-slate-400"} />
            Stokta Var
          </button>
          <BrandDropdown brands={brands} selected={brandFilter} onSelect={setBrandFilter} loading={brandsLoading} />
        </div>
      </div>

      {/* Aktif filtreler */}
      {(inStock || brandFilter) && (
        <div className="px-5 pt-2.5 flex flex-wrap gap-2">
          {inStock && (
            <span className="flex items-center gap-1.5 text-xs font-semibold bg-green-100 text-green-700 border border-green-200 px-3 py-1.5 rounded-full">
              <CheckCircle2 size={12} /> Stokta Var
              <button onClick={() => setInStock(false)} className="ml-1 text-green-500 hover:text-green-700">✕</button>
            </span>
          )}
          {brandFilter && (
            <span className="flex items-center gap-1.5 text-xs font-semibold bg-[#0D1B3E]/10 text-[#0D1B3E] border border-[#0D1B3E]/20 px-3 py-1.5 rounded-full">
              <Package size={12} /> {brandFilter}
              <button onClick={() => setBrandFilter("")} className="ml-1 opacity-60 hover:opacity-100">✕</button>
            </span>
          )}
        </div>
      )}

      {importedIds && <div className="mx-5 mt-3 flex items-center justify-between rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"><span>Bu aktarımda yeni eklenen stok kartları düzenlemeye hazır.</span><button onClick={() => setUrlParams({})} className="font-semibold hover:underline">Tüm stoklara dön</button></div>}
      {activeSearch && alternativeCount > 0 && !alternativesOnly && (
        <div className="mx-5 mt-2 flex min-h-9 items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900"><span className="font-semibold">Stokta {alternativeCount} alternatif ürün var</span><button type="button" onClick={() => setAlternativesOnly(true)} className="shrink-0 rounded-md bg-emerald-600 px-3 py-1 font-semibold text-white hover:bg-emerald-700">Göster</button></div>
      )}
      {activeSearch && alternativesOnly && (
        <div className="mx-5 mt-2 flex min-h-9 items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-900"><span className="font-semibold">Stoklu alternatif ürünler gösteriliyor</span><button type="button" onClick={() => setAlternativesOnly(false)} className="shrink-0 font-semibold underline">Tüm sonuçlara dön</button></div>
      )}
      {/* ── Ürün kartları ────────────────────────────────────────────────────── */}
      <div className="px-5 pt-2 pb-4 space-y-3" data-testid={STOK.listTable}>
        {loading && stoklar.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : stoklar.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">Kayıt bulunamadı</div>
        ) : displayedStoklar.map((s) => {
          const prices = calcPrices(s, eurKuru, fiyatListeleri);
          return (
            <div key={s.id} className={`relative min-w-0 bg-white border border-slate-200 rounded-xl p-4 lg:pl-[124px] lg:pr-[324px] shadow-sm ${hasOemValue(s.oem_no2) ? "lg:min-h-[172px]" : "lg:min-h-[148px]"}`}>
              <LogoSettingsLink brand={s.marka} onOpen={openLogoSettings} className="absolute left-4 top-4" />
              <div aria-hidden="true" className="absolute bottom-4 left-[108px] top-4 hidden border-l border-slate-200 lg:block" />
              {isDesktop && (
                <div className="absolute left-4 top-[68px]"><StockBarcode value={s.barkod} /></div>
              )}

              {/* Kod + Marka */}
              <div className="mb-3 ml-[92px] min-h-[68px] lg:mb-1.5 lg:ml-0 lg:min-h-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="font-mono text-sm font-bold text-[#0D1B3E] tracking-wide" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {s.kod}
                  </span>
                  <BrandBadge brand={s.marka} />
                  {Number(s.kampanya_iskonto || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                      <BadgePercent size={12}/> Kampanya %{fmt(Number(s.kampanya_iskonto), 2)}
                    </span>
                  )}
                </div>
                <div className="mt-2 font-semibold leading-snug text-slate-900 lg:hidden" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {s.ad}
                </div>
              </div>

              {/* Ana OEM */}
              {s.oem_kodu && (
                <div className="text-xs font-mono font-semibold text-blue-600 leading-snug break-all mb-2 select-text">
                  {s.oem_kodu}
                </div>
              )}

              {/* Ana satır */}
              <div className="grid min-w-0 grid-cols-[72px_minmax(0,1fr)] items-start gap-x-4 gap-y-4 lg:flex lg:items-start lg:gap-3">

                {/* Sol: ürün bilgisi */}
                <div className="col-span-2 min-w-0 flex-1 lg:col-auto">
                  <div className="hidden max-w-full break-words font-semibold text-slate-900 text-base leading-snug [overflow-wrap:anywhere] lg:block" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {s.ad}
                  </div>

                  {/* OEM alanları */}
                  <div className="relative mt-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wide w-20 shrink-0 whitespace-nowrap">Değişen No:</span>
                      <span className={`text-xs font-mono ${s.degisen_no ? "text-slate-700 font-medium" : "text-slate-300"}`}>{s.degisen_no || "—"}</span>
                    </div>
                    {hasNoOemMatch(s) && <div className="text-xs font-medium text-slate-400">Henüz OEM eşleşmesi bulunamadı.</div>}
                    {!hasNoOemMatch(s) && <div className={`flex items-center gap-2 sm:grid sm:grid-cols-[80px_minmax(0,1fr)] sm:items-start ${hasOemValue(s.oem_no1) ? "" : "sm:hidden"}`}>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wide w-20 shrink-0 whitespace-nowrap">OEM NO1:</span>
                      <div className="min-w-0 sm:leading-4">
                        <span className={`text-xs font-mono font-semibold leading-snug break-words select-text ${s.oem_no1 ? "text-blue-600" : "text-slate-300"}`} title={s.oem_no1 || ""}><ResponsiveOemValue value={s.oem_no1} /></span>
                        {s.oem_no1?.length > MOBILE_OEM_CHAR_LIMIT && <button onClick={() => setOemModal(s.oem_no1)} className="sm:hidden ml-1 text-xs text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}
                        {s.oem_no1?.length > OEM_CHAR_LIMIT && <button onClick={() => setOemModal(s.oem_no1)} className="hidden sm:inline ml-1 text-xs text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}
                      </div>
                    </div>}
                    {!hasNoOemMatch(s) && <div className={`flex items-start gap-2 sm:grid sm:grid-cols-[80px_minmax(0,1fr)] ${hasOemValue(s.oem_no2) ? "" : "sm:hidden"}`}>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wide w-20 shrink-0 whitespace-nowrap sm:mt-0">OEM NO2:</span>
                      <div className="flex-1 min-w-0 sm:overflow-hidden sm:leading-4">
                        <span className={`text-xs font-mono font-semibold leading-snug break-all select-text ${s.oem_no2 ? "text-blue-600" : "text-slate-300"}`}><ResponsiveOemValue value={s.oem_no2} /></span>
                        {s.oem_no2?.length > MOBILE_OEM_CHAR_LIMIT && <button onClick={() => setOemModal(s.oem_no2)} className="sm:hidden ml-1 text-xs text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}
                        {s.oem_no2?.length > OEM_CHAR_LIMIT && <button onClick={() => setOemModal(s.oem_no2)} className="hidden sm:inline ml-1 text-xs text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}
                      </div>
                    </div>}
                  </div>
                </div>

                {/* Orta: stok göstergesi */}
                <div className="flex w-[72px] shrink-0 flex-col items-center justify-start gap-0 pt-1 lg:absolute lg:right-[287px] lg:top-4 lg:w-auto lg:pt-0">
                  <StockBadge qty={s._effectiveStock ?? s.stok_miktari} />
                  <span className="text-[10px] text-slate-400 tabular-nums mt-1">{fmt(s._effectiveStock ?? s.stok_miktari ?? 0, 0)} {s.birim}</span>
                </div>

                {/* Sağ: 3 grup fiyatı + aksiyonlar */}
                <div className="min-w-0 w-full shrink-0 flex flex-col items-end lg:absolute lg:right-4 lg:top-4 lg:w-[252px]">
                  {prices ? (
                    <div className="w-full space-y-1.5 mb-2">
                      {prices.map(({ label, price, regularPrice, isOrijinal: io }) => (
                        <div key={label} className="grid grid-cols-[132px_minmax(0,1fr)] items-center gap-2">
                          <span className={`whitespace-nowrap text-right text-[10px] font-semibold uppercase tracking-wider leading-tight ${io ? "text-amber-600" : "text-slate-400"}`}>
                            {label}{io ? " (Net)" : ""}
                          </span>
                          <span className="flex min-w-0 items-baseline justify-end gap-1 whitespace-nowrap text-right">
                            {price < regularPrice && <span className="text-[10px] text-slate-400 line-through">{fmtCurrency(regularPrice)}</span>}
                            <span className="text-sm font-bold tabular-nums text-[#0D1B3E]">{fmtCurrency(price)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 italic mb-2 w-full text-right">Fiyat yok</div>
                  )}

                  <div className="mb-1.5 grid w-full grid-cols-[132px_minmax(0,1fr)] gap-2">
                    <div className="whitespace-nowrap text-right text-[10px] font-semibold tracking-wider leading-tight text-amber-600">Paket: {getPack(s)} adet{getPack(s) > 1 ? " ve katları" : ""}</div>
                    <span aria-hidden="true" />
                  </div>
                  <div className="flex w-full items-center justify-end gap-1.5">
                    {/* Edit / Delete — mobile hidden */}
                    <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
                      <button onClick={() => openEdit(s)}
                        className="flex h-8 items-center gap-1 text-xs text-slate-500 hover:text-blue-600 border border-slate-200 hover:border-blue-300 rounded-lg px-2 py-1 transition-colors">
                        <Pencil size={12} /> Düzenle
                      </button>
                      <button onClick={() => handleDelete(s.id)}
                        className="flex h-8 items-center gap-1 text-xs text-slate-500 hover:text-red-500 border border-slate-200 hover:border-red-300 rounded-lg px-2 py-1 transition-colors">
                        <Trash2 size={12} /> Sil
                      </button>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <input type="number" min={getPack(s)} step={getPack(s)} value={getQty(s)} onChange={(e) => setQty(s, e.target.value)} onClick={(e) => e.target.select()} aria-label={`${s.kod} sipariş miktarı`} className={`h-8 w-12 rounded-lg border border-slate-300 bg-white text-center text-xs font-semibold tabular-nums ${(s._effectiveStock ?? s.stok_miktari ?? 0) <= 0 ? "opacity-50" : ""}`} />
                      <button type="button" onClick={() => handleAddToCart(s, prices)} className={`flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs font-semibold transition-colors ${(s._effectiveStock ?? s.stok_miktari ?? 0) <= 0 ? "bg-slate-400 text-white opacity-60 hover:bg-slate-500" : isInCart(s.id) ? "border border-green-200 bg-green-100 text-green-700" : "bg-[#0D1B3E] text-white hover:bg-[#172A58]"}`}>
                        <ShoppingCart size={12}/> {isInCart(s.id) ? "Sepette" : "Ekle"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {s._alternatives?.length > 0 && (
                <details className="mt-4 border-t border-slate-100 pt-3 lg:mr-[-292px]">
                  <summary className="cursor-pointer text-sm font-semibold text-[#0D1B3E]">Stokta olmayan {s._alternatives.length} {s.marka} ürünü göster · Farklı fiyatlar mevcut</summary>
                  <div className="mt-3 space-y-3">
                    {s._alternatives.map((alt) => {
                      const altPrices = calcPrices(alt, eurKuru, fiyatListeleri);
                      return <div key={alt.id} className="relative min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <LogoSettingsLink brand={alt.marka} onOpen={openLogoSettings} className="absolute left-4 top-4 shrink-0 lg:hidden" />
                        <div className="min-w-0 lg:hidden">
                          <div className="mb-3 ml-[92px] min-h-[68px]">
                            <div className="flex flex-wrap items-center gap-2"><span className="font-mono font-bold">{alt.kod}</span><BrandBadge brand={alt.marka} /></div>
                            <div className="mt-2 font-semibold leading-snug text-slate-900">{alt.ad}</div>
                          </div>
                          <div className="grid min-w-0 grid-cols-[72px_minmax(0,1fr)] items-start gap-x-4 gap-y-4 lg:flex lg:items-start lg:gap-4">
                            <div className="col-span-2 min-w-0 flex-1 overflow-hidden lg:col-auto">
                              <div className="mt-2 grid grid-cols-[80px_minmax(0,1fr)] gap-2 text-xs text-slate-500 lg:mt-0"><span>Değişen No:</span><span className="font-mono select-text">{alt.degisen_no || "—"}</span></div>
                              {hasNoOemMatch(alt) ? <div className="text-xs font-medium text-slate-400">Henüz OEM eşleşmesi bulunamadı.</div> : <>
                                <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-2 text-xs text-slate-500"><span>OEM NO1:</span><span className="min-w-0 font-mono text-blue-600 break-words select-text" title={alt.oem_no1 || ""}><ResponsiveOemValue value={alt.oem_no1} /></span></div>
                                <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-2 text-xs text-slate-500"><span>OEM NO2:</span><div className="min-w-0"><span className="font-mono text-blue-600 break-words select-text"><ResponsiveOemValue value={alt.oem_no2} /></span>{alt.oem_no2?.length > MOBILE_OEM_CHAR_LIMIT && <button onClick={() => setOemModal(alt.oem_no2)} className="sm:hidden ml-1 text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}{alt.oem_no2?.length > OEM_CHAR_LIMIT && <button onClick={() => setOemModal(alt.oem_no2)} className="hidden sm:inline ml-1 text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}</div></div>
                              </>}
                            </div>
                            <div className="flex w-[72px] shrink-0 flex-col items-center lg:w-[72px]"><StockBadge qty={alt._effectiveStock ?? alt.stok_miktari} /><span className="mt-1 text-[10px] text-slate-400">{fmt(alt._effectiveStock ?? alt.stok_miktari ?? 0, 0)} {alt.birim}</span></div>
                            <div className="min-w-0 w-full shrink-0 lg:w-[252px]">
                              {altPrices?.map(({ label, price, isOrijinal }) => <div key={label} className="mb-1 grid grid-cols-[132px_minmax(0,1fr)] items-center gap-2"><span className={`whitespace-nowrap text-right text-[10px] font-semibold uppercase tracking-wider ${isOrijinal ? "text-amber-600" : "text-slate-400"}`}>{label}{isOrijinal ? " (Net)" : ""}</span><span className="whitespace-nowrap text-right text-sm font-bold">{fmtCurrency(price)}</span></div>)}
                              <div className="mb-1.5 grid w-full grid-cols-[132px_minmax(0,1fr)] gap-2"><div className="whitespace-nowrap text-right text-[10px] font-semibold tracking-wider leading-tight text-amber-600">Paket: {getPack(alt)} adet{getPack(alt) > 1 ? " ve katları" : ""}</div><span aria-hidden="true" /></div>
                              <div className="flex w-full items-center justify-end gap-1.5">
                                <div className="hidden shrink-0 items-center gap-1.5 sm:flex"><button onClick={() => openEdit(alt)} className="flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-blue-300 hover:text-blue-600"><Pencil size={12}/>Düzenle</button><button onClick={() => handleDelete(alt.id)} className="flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-red-300 hover:text-red-500"><Trash2 size={12}/>Sil</button></div>
                                <input type="number" min={getPack(alt)} step={getPack(alt)} value={getQty(alt)} onChange={(e) => setQty(alt, e.target.value)} className={`h-8 w-12 rounded-lg border text-center text-xs ${(alt._effectiveStock ?? alt.stok_miktari ?? 0) <= 0 ? "opacity-50" : ""}`} />
                                <button onClick={() => handleAddToCart(alt, altPrices)} className={`flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs text-white ${(alt._effectiveStock ?? alt.stok_miktari ?? 0) <= 0 ? "bg-slate-400 opacity-60" : "bg-[#0D1B3E]"}`}><ShoppingCart size={12}/>Ekle</button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="hidden grid-cols-[100px_minmax(0,1fr)] items-start gap-6 lg:grid">
                          <div className="h-full shrink-0 border-r border-slate-200 pr-4"><LogoSettingsLink brand={alt.marka} onOpen={openLogoSettings} />{isDesktop && <StockBarcode value={alt.barkod} />}</div>
                          <div className="flex min-w-0 gap-4 items-start">
                            <div className="flex-1 min-w-0 overflow-hidden"><div className="flex items-center gap-2"><span className="font-mono font-bold">{alt.kod}</span><BrandBadge brand={alt.marka} /></div><div className="font-semibold text-slate-900 mt-1">{alt.ad}</div><div className="mt-2 grid grid-cols-[80px_minmax(0,1fr)] gap-2 text-xs text-slate-500"><span>Değişen No:</span><span className="font-mono select-text">{alt.degisen_no || "—"}</span></div>{hasNoOemMatch(alt) && <div className="text-xs font-medium text-slate-400">Henüz OEM eşleşmesi bulunamadı.</div>}{hasOemValue(alt.oem_no1) && <div className="grid grid-cols-[80px_minmax(0,1fr)] items-start gap-2 text-xs text-slate-500"><span>OEM NO1:</span><span className="font-mono text-blue-600 break-all select-text" title={alt.oem_no1 || ""}><ResponsiveOemValue value={alt.oem_no1} /></span></div>}{hasOemValue(alt.oem_no2) && <div className="grid min-w-0 grid-cols-[80px_minmax(0,1fr)] items-start gap-2 text-xs text-slate-500"><span>OEM NO2:</span><span className="min-w-0 overflow-hidden break-all"><span className="font-mono text-blue-600 break-all select-text"><ResponsiveOemValue value={alt.oem_no2} /></span>{alt.oem_no2?.length > OEM_CHAR_LIMIT && <button onClick={() => setOemModal(alt.oem_no2)} className="ml-1 text-[#0D1B3E] underline font-medium whitespace-normal">(tümü için tıklayın)</button>}</span></div>}</div>
                            <div className="flex shrink-0 flex-col items-center w-[72px]"><StockBadge qty={alt._effectiveStock ?? alt.stok_miktari} /><span className="mt-1 text-[10px] text-slate-400">{fmt(alt._effectiveStock ?? alt.stok_miktari ?? 0, 0)} {alt.birim}</span></div>
                            <div className="w-[252px] shrink-0">
                              {altPrices?.map(({ label, price, isOrijinal }) => <div key={label} className="mb-1 grid grid-cols-[132px_minmax(0,1fr)] items-center gap-2"><span className={`whitespace-nowrap text-right text-[10px] font-semibold uppercase tracking-wider ${isOrijinal ? "text-amber-600" : "text-slate-400"}`}>{label}{isOrijinal ? " (Net)" : ""}</span><span className="whitespace-nowrap text-right text-sm font-bold">{fmtCurrency(price)}</span></div>)}
                              <div className="mb-1.5 grid w-full grid-cols-[132px_minmax(0,1fr)] gap-2"><div className="whitespace-nowrap text-right text-[10px] font-semibold tracking-wider leading-tight text-amber-600">Paket: {getPack(alt)} adet{getPack(alt) > 1 ? " ve katları" : ""}</div><span aria-hidden="true" /></div>
                              <div className="flex w-full items-center justify-end gap-1.5">
                                <div className="flex shrink-0 items-center gap-1.5"><button onClick={() => openEdit(alt)} className="flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-blue-300 hover:text-blue-600"><Pencil size={12}/>Düzenle</button><button onClick={() => handleDelete(alt.id)} className="flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-red-300 hover:text-red-500"><Trash2 size={12}/>Sil</button></div>
                                <input type="number" min={getPack(alt)} step={getPack(alt)} value={getQty(alt)} onChange={(e) => setQty(alt, e.target.value)} className={`h-8 w-12 rounded-lg border text-center text-xs ${(alt._effectiveStock ?? alt.stok_miktari ?? 0) <= 0 ? "opacity-50" : ""}`} />
                                <button onClick={() => handleAddToCart(alt, altPrices)} className={`flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs text-white ${(alt._effectiveStock ?? alt.stok_miktari ?? 0) <= 0 ? "bg-slate-400 opacity-60" : "bg-[#0D1B3E]"}`}><ShoppingCart size={12}/>Ekle</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>;
                    })}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Sayfalama ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-4">
        <span className="text-sm text-slate-500">{total.toLocaleString("tr-TR")} {alternativesOnly ? "stoklu alternatif" : "ürün"}</span>
        {activeSearch ? (
          <div ref={loadMoreRef} className="text-sm text-slate-400">
            {page < pages ? (loading ? "Sonraki 10 ürün yükleniyor…" : "Devamı kaydırdıkça yüklenecek") : "Tüm sonuçlar gösterildi"}
          </div>
        ) : <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="p-2.5 rounded-xl text-slate-400 hover:text-slate-700 disabled:opacity-40 border border-slate-200 bg-white">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-slate-600 px-2 tabular-nums">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            className="p-2.5 rounded-xl text-slate-400 hover:text-slate-700 disabled:opacity-40 border border-slate-200 bg-white">
            <ChevronRight size={16} />
          </button>
        </div>}
      </div>
      {activeSearch && (
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Sayfanın başına dön" title="Yukarı dön"
          className="fixed right-5 bottom-20 z-30 rounded-full bg-[#0D1B3E] p-3 text-white shadow-lg hover:bg-[#172A58]">
          <ArrowUp size={18} />
        </button>
      )}

      {suggestion && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSuggestion(null)}>
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4"><div><h2 className="font-bold text-[#0D1B3E]">Arama OEM'i öner</h2><p className="mt-1 text-xs text-slate-500">{catalogFilter?.vehicleLabel} · {catalogFilter?.category}{catalogFilter?.subcategory ? ` / ${catalogFilter.subcategory}` : ""}{catalogFilter?.detail ? ` / ${catalogFilter.detail}` : ""}</p></div><button onClick={() => setSuggestion(null)} className="text-slate-400"><X size={18}/></button></div>
          <div className="space-y-4 p-5">
            {catalogFilter?.primaryOem && <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">Mevcut arama OEM'i: <b className="font-mono">{catalogFilter.primaryOem}</b></div>}
            <label className="block text-xs font-bold text-slate-600">Önerilen arama OEM'i<input autoFocus value={suggestion.oem} onChange={(event) => setSuggestion((current) => ({ ...current, oem: event.target.value.toUpperCase() }))} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 font-mono text-sm font-normal" placeholder="Örn. 03L115562"/></label>
            <label className="block text-xs font-bold text-slate-600">Açıklama<textarea value={suggestion.note} onChange={(event) => setSuggestion((current) => ({ ...current, note: event.target.value }))} className="mt-1 min-h-24 w-full rounded-lg border border-slate-200 p-3 text-sm font-normal" placeholder="Neden kontrol edilmesi gerektiğini veya kaynağı yazın"/></label>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4"><button onClick={() => setSuggestion(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Vazgeç</button><button disabled={!suggestion.oem.trim()} onClick={saveSuggestion} className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white disabled:bg-slate-300">Öneriyi kaydet</button></div>
        </div>
      </div>}

      {/* ── OEM NO2 tüm numaralar modalı ─────────────────────────────────── */}
      {oemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOemModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-[#0D1B3E]">OEM NO2 — Tüm Numaralar</h2>
                <p className="text-xs text-slate-400 mt-1">{oemModal.split(",").filter((code) => code.trim()).length} numara</p>
              </div>
              <button onClick={() => setOemModal(null)} className="text-slate-400 hover:text-slate-700" aria-label="Kapat"><X size={18} /></button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto px-5">
              {oemModal.split(",").map((code) => code.trim()).filter(Boolean).map((code, index) => (
                <div key={`${code}-${index}`} className="py-3 border-b border-slate-100 last:border-0 font-mono text-xs font-semibold text-blue-600 break-all select-text">
                  {code}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-200 grid grid-cols-2 gap-2">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(oemModal);
                    toast.success("OEM numaraları kopyalandı");
                  } catch {
                    toast.error("Kopyalama işlemi başarısız oldu");
                  }
                }}
                className="rounded-xl border border-[#0D1B3E] px-4 py-2.5 text-sm font-semibold text-[#0D1B3E] hover:bg-slate-50"
              >
                Tümünü Kopyala
              </button>
              <button onClick={() => setOemModal(null)} className="rounded-xl bg-[#0D1B3E] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#172A58]">Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stok oluştur / düzenle modal ────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {editItem ? "Stok Düzenle" : "Yeni Stok"}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700 text-lg">✕</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[["Stok Kodu *","kod"],["Stok Adı *","ad"],["Barkod Numarası","barkod"],["OEM Kodu (eski)","oem_kodu"],["Birim","birim"]].map(([lbl, name]) => (
                  <div key={name}>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{lbl}</label>
                    <input value={form[name] ?? ""} required={lbl.includes("*")}
                      onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Stok Miktarı</label>
                  <input type="number" value={form.stok_miktari ?? 0}
                    onChange={(e) => setForm((p) => ({ ...p, stok_miktari: parseFloat(e.target.value) || 0 }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Paket / Minimum Sipariş Adedi</label>
                  <input type="number" min="1" step="1" value={form.paket_adedi ?? 1}
                    onChange={(e) => setForm((p) => ({ ...p, paket_adedi: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Vitrin Sırası</label>
                  <input type="number" min="0" value={form.vitrin_sira ?? 0} onChange={(e) => setForm((p) => ({ ...p, vitrin_sira: parseInt(e.target.value, 10) || 0 }))} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <label className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800"><input type="checkbox" checked={Boolean(form.vitrin)} onChange={(e) => setForm((p) => ({ ...p, vitrin: e.target.checked }))} className="h-4 w-4"/> Vitrinde göster</label>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Kampanya Ek İskonto %</label>
                  <input data-testid="campaign-discount-input" type="number" min="0" max="100" step="0.01" value={form.kampanya_iskonto ?? 0}
                    onChange={(e) => setForm((p) => ({ ...p, kampanya_iskonto: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Marka</label>
                  <input list="stok-marka-listesi" value={form.marka || ""}
                    onChange={(e) => setForm((p) => ({ ...p, marka: e.target.value.toLocaleUpperCase("tr-TR") }))}
                    placeholder="Marka seçin veya yazın"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                  <datalist id="stok-marka-listesi">
                    {brands.map((brand) => <option key={brand} value={brand} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Para Birimi</label>
                  <select value={form.para_birimi || "TL"} onChange={(e) => setForm((p) => ({ ...p, para_birimi: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20">
                    <option value="TL">TL</option><option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">TL Fiyat</label>
                  <input type="number" step="0.01" value={form.tl_fiyat ?? ""}
                    onChange={(e) => setForm((p) => ({ ...p, tl_fiyat: parseFloat(e.target.value) || null }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">EUR Fiyat</label>
                  <input type="number" step="0.01" value={form.eur_fiyat ?? ""}
                    onChange={(e) => setForm((p) => ({ ...p, eur_fiyat: parseFloat(e.target.value) || null }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
              </div>
              {/* OEM alanları */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2">OEM & Referans</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[["OEM No 1","oem_no1"],["Değişen No","degisen_no"],["Ek Bilgi","ek_bilgi"],["OEM No 2","oem_no2"]].map(([lbl,name]) => (
                    <div key={name}>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{lbl}</label>
                      <input value={form[name] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors">
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
